export type ProductType = "bed" | "sofa" | "sectional" | "chair" | "ottoman" | "pillow" | "unknown";

/**
 * Broad product type derived from a SKU, used to show a matching icon in the SKU picker.
 *
 * Prefixes: SP -> pillow, DB -> bed. For DL the item-type code sits at chars 5-8 of the short
 * SKU (category + style + item_type + depth + comfort); we classify by its 2-digit family and
 * the LF/RF facing suffix rather than matching the full catalog code, so unlisted-but-valid
 * variants (e.g. a "75LF" chaise) still resolve. Reference: src/routes/postgresql/data/itemTypeData.ts.
 */
const SOFA_FAMILIES = new Set(["30", "70", "60", "20"]); // Sofa, Sofa Plus, Loft Sofa, Loveseat
const CHAIR_FAMILIES = new Set(["01", "00"]); // Arm Chair, Cuddle Chair
const OTTOMAN_FAMILIES = new Set(["02", "38"]); // Ottoman, Cuddle Ottoman
const SECTIONAL_FAMILIES = new Set(["15", "11", "10", "03"]); // Corner Unit, Cuddle Corner, Armless Chair, Armless Loft Sofa

export const getProductType = (sku: string): ProductType => {
  const s = (sku || "").trim().toUpperCase();
  if (!s) return "unknown";

  if (s.startsWith("SP")) return "pillow";
  if (s.startsWith("DB")) return "bed";

  if (s.startsWith("DL")) {
    const family = s.slice(5, 7); // 2-digit item-type family
    const facing = s.slice(7, 9); // LF / RF for directional sectional modules

    if (facing === "LF" || facing === "RF") return "sectional";
    if (SOFA_FAMILIES.has(family)) return "sofa";
    if (CHAIR_FAMILIES.has(family)) return "chair";
    if (OTTOMAN_FAMILIES.has(family)) return "ottoman";
    if (SECTIONAL_FAMILIES.has(family)) return "sectional";
    return "unknown";
  }

  return "unknown";
};
